import { Grid } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import DialogComponent from "../../../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../../../common/components/functional/dialogs/DialogContentWrapper";
import { useConfirmationDialog } from "../../../../../contexts/ConfirmationDialogContext";
import { Employment } from "../../../../employment/employmentApi";
import {
  disableTargetedShifts,
  updateTargetedShiftSpec,
} from "../../../../employment/employmentSlice";
import { fetchEmpTargetedShiftSpec } from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesActions";
import { TargetedShiftSpec } from "../../../../employment/targetedShiftSalaries/targetedShiftSalariesApi";
import TargetedShiftSalaries from "./TargetedShiftSalaries";
import { TargetedShifts } from "./TargetedShifts";
import { selectAssignmentsByEmploymentId } from "../../AssignmentAdditionalInfo/AssignmentsSlice";

interface Props {
  open: boolean;
  onClose: () => void;
  employment: Employment;
  canEdit: boolean;
}

const EMPTY_SPEC: TargetedShiftSpec = {
  version: 0,
  targetingRules: [],
  salaries: [],
  variationValidationAssignmentId: null,
};

const TargetedShiftSettingsDialog = ({
  open,
  onClose,
  employment,
  canEdit,
}: Props) => {
  const { t } = useTranslation(["talentProfile", "common"]);
  const dispatch = useAppDispatch();
  const { confirm } = useConfirmationDialog();
  const specVersion = useAppSelector(
    (s) => s.employment.additionalInfo?.targetedShiftSpec?.version ?? 0,
  );
  const reduxTargetedShiftSpec = useAppSelector(
    (s) => s.employment.additionalInfo?.targetedShiftSpec,
  );
  const assignments = useAppSelector(
    selectAssignmentsByEmploymentId(employment.employmentId),
  );
  const [spec, setSpec] = useState<TargetedShiftSpec | null>(null);
  const [isDisabling, setIsDisabling] = useState(false);
  const [rulesCount, setRulesCount] = useState(0);

  const contractId = useMemo(() => {
    if (!spec?.variationValidationAssignmentId) return undefined;
    return assignments.find(
      (a) => a.assignmentId === spec.variationValidationAssignmentId,
    )?.details?.contractId;
  }, [spec?.variationValidationAssignmentId, assignments]);

  useEffect(() => {
    if (open && employment.employmentId) {
      dispatch(
        fetchEmpTargetedShiftSpec({ employmentId: employment.employmentId }),
      ).then((action) => {
        if (isFulfilled(action)) {
          const payload = action.payload ?? EMPTY_SPEC;
          setSpec(payload);
          setRulesCount(payload.targetingRules.length);
          dispatch(updateTargetedShiftSpec(payload));
        } else {
          setSpec(EMPTY_SPEC);
        }
      });
    } else {
      setSpec(null);
      setRulesCount(0);
    }
  }, [open, employment.employmentId, dispatch]);

  // Keep this local snapshot in sync with the Redux-held spec, since child
  // components update it there (not here) after saving changes.
  useEffect(() => {
    if (!reduxTargetedShiftSpec || !spec) return;
    if (reduxTargetedShiftSpec.version > spec.version) {
      setSpec(reduxTargetedShiftSpec);
      setRulesCount(reduxTargetedShiftSpec.targetingRules.length);
    }
  }, [reduxTargetedShiftSpec, spec]);

  const hasRules = rulesCount > 0;

  const handleRulesChanged = (count: number) => {
    setRulesCount(count);
  };

  const handleDisable = async () => {
    const confirmed = await confirm({
      title: t("talentProfile:disableTargetedShifts"),
      contentText: t("talentProfile:confirmDisableTargetedShifts"),
      confirmText: t("common:delete"),
      cancelText: t("common:cancel"),
      ConfirmButtonProps: { color: "error" },
    });
    if (!confirmed) return;

    setIsDisabling(true);
    try {
      const action = await dispatch(
        disableTargetedShifts({
          employmentId: employment.employmentId,
          version: specVersion,
        }),
      );
      if (isFulfilled(action)) {
        onClose();
      }
    } finally {
      setIsDisabling(false);
    }
  };

  return (
    <DialogComponent
      open={open}
      onClose={onClose}
      sx={{ maxWidth: "950px", width: "90%" }}
    >
      <DialogContentWrapper
        title={t("talentProfile:targetedShiftSettingsDialogTitle")}
        onCancel={onClose}
        customCancelButtonLabel={t("common:close")}
        onDelete={canEdit && hasRules ? handleDisable : undefined}
        customDeleteButtonLabel={t("talentProfile:disableTargetedShifts")}
        isLoading={spec === null || isDisabling}
      >
        <Grid item xs={12}>
          <TargetedShifts
            employment={employment}
            canEdit={canEdit}
            infoTooltip={t("talentProfile:targetedShifts.tooltipText")}
            initialSpec={spec ?? undefined}
            onRulesChanged={handleRulesChanged}
          />
        </Grid>
        <Grid item xs={12}>
          <TargetedShiftSalaries
            employmentId={employment.employmentId}
            tesId={employment.tesId}
            talentId={employment.talentId}
            workTitleId={employment.details?.workTitleId ?? ""}
            canEdit={canEdit}
            noBorder
            initialSpec={spec ?? undefined}
            hasTargetingRules={rulesCount > 0}
            contractId={contractId}
            employmentStart={employment.details?.agreedStart}
            employmentEnd={employment.details?.agreedEnd}
          />
        </Grid>
      </DialogContentWrapper>
    </DialogComponent>
  );
};

export default TargetedShiftSettingsDialog;
